import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ZehumaLogo from '../portal/ZehumaLogo';
import { searchPortalOrganizations, getImageUrl } from '../../utils/api';

function clipDescription(text, max = 120) {
    if (!text || typeof text !== 'string') return '';
    const plain = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    if (plain.length <= max) return plain;
    return `${plain.slice(0, max)}…`;
}

/**
 * Menü yüklenemedi / restoran bulunamadı — portal ile uyumlu vurgulu hata ekranı + arama
 */
export default function MenuLoadErrorView({ variant, message, onRetry }) {
    const navigate = useNavigate();
    const isNotFound = variant === 'not_found';
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        document.title = isNotFound ? 'Restoran bulunamadı | Zehuma QR Menü' : 'Menü yüklenemedi | Zehuma QR Menü';
    }, [isNotFound]);

    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) {
            setResults([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const res = await searchPortalOrganizations(q);
                setResults(Array.isArray(res.data) ? res.data : []);
                setOpen(true);
            } catch (e) {
                console.error(e);
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 200);
        return () => clearTimeout(t);
    }, [query]);

    useEffect(() => {
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    const goMenu = (s) => {
        setOpen(false);
        navigate(`/${s}`);
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 text-slate-900">
            <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/95 backdrop-blur-md">
                <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
                    <ZehumaLogo />
                    <Link
                        to="/"
                        className="text-sm font-medium text-violet-700 transition hover:text-violet-900 hover:underline"
                    >
                        Ana sayfa
                    </Link>
                </div>
            </header>

            <main className="mx-auto max-w-lg px-4 py-10 sm:py-14">
                <div
                    className="overflow-hidden rounded-3xl border-2 border-amber-200/90 bg-white shadow-xl shadow-amber-500/10 ring-1 ring-slate-900/5"
                    role="alert"
                >
                    <div className="border-b border-amber-100 bg-gradient-to-r from-amber-50 via-orange-50/80 to-violet-50 px-5 py-4 sm:px-6">
                        <div className="flex items-start gap-3">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 ring-1 ring-amber-200/80">
                                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                    />
                                </svg>
                            </span>
                            <div>
                                <p className="text-xs font-bold uppercase tracking-wider text-amber-800/90">Hata</p>
                                <h1 className="mt-0.5 text-xl font-extrabold tracking-tight text-slate-900 sm:text-2xl">
                                    {isNotFound ? 'Restoran bulunamadı' : 'Menü yüklenemedi'}
                                </h1>
                                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                                    {isNotFound
                                        ? 'Bu bağlantıya ait bir QR menü yok ya da menü geçici olarak kullanılamıyor olabilir. Aşağıdan işletme arayabilir veya ana sayfaya dönebilirsiniz.'
                                        : (message || 'Bağlantı veya sunucu kaynaklı bir sorun oluştu. Bir süre sonra tekrar deneyebilirsiniz.')}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-5 px-5 py-6 sm:px-6">
                        <div>
                            <p className="mb-2 text-sm font-semibold text-slate-800">İşletme ara</p>
                            <p className="mb-3 text-xs text-slate-500">En az 2 karakter yazın; listeden seçerek menüye gidin.</p>
                            <div ref={wrapRef} className="relative z-20">
                                <div className="flex rounded-2xl border border-slate-200 bg-white shadow-md shadow-violet-500/5 ring-1 ring-slate-100 focus-within:ring-2 focus-within:ring-violet-500/30">
                                    <span className="flex items-center pl-3 text-slate-400" aria-hidden="true">
                                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </span>
                                    <input
                                        type="search"
                                        autoComplete="off"
                                        placeholder="Restoran adı…"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        onFocus={() => query.trim().length >= 2 && setOpen(true)}
                                        className="min-h-[48px] flex-1 border-0 bg-transparent px-2 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0"
                                    />
                                    {loading && (
                                        <span className="flex items-center pr-3 text-violet-600">
                                            <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                        </span>
                                    )}
                                </div>

                                {open && query.trim().length >= 2 && (
                                    <ul
                                        className="absolute left-0 right-0 top-full z-[70] mt-2 max-h-[min(55vh,22rem)] overflow-auto rounded-2xl border border-slate-200 bg-white py-1.5 text-left shadow-xl ring-1 ring-slate-900/5"
                                        role="listbox"
                                    >
                                        {results.length === 0 && !loading && (
                                            <li className="px-3 py-2.5 text-sm text-slate-500">Sonuç yok.</li>
                                        )}
                                        {results.map((org) => {
                                            const desc = clipDescription(org.restaurant_description);
                                            const logoPath = org.display_logo || org.restaurant_logo;
                                            const showLogo = Boolean(logoPath && String(logoPath).trim());
                                            const qrLive = Boolean(org.qr_menu_active);
                                            return (
                                                <li key={org.id}>
                                                    <button
                                                        type="button"
                                                        className="flex w-full items-stretch gap-2.5 px-2.5 py-2.5 text-left text-sm transition hover:bg-violet-50 active:bg-violet-100"
                                                        onClick={() => goMenu(org.slug)}
                                                    >
                                                        <div className="relative h-11 w-11 shrink-0 self-center overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                                                            {showLogo ? (
                                                                <img
                                                                    src={getImageUrl(logoPath)}
                                                                    alt=""
                                                                    className="h-full w-full object-cover"
                                                                    loading="lazy"
                                                                    decoding="async"
                                                                />
                                                            ) : (
                                                                <div className="flex h-full w-full items-center justify-center text-sm font-bold text-violet-400">
                                                                    {(org.name || '?').charAt(0).toUpperCase()}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="min-w-0 flex-1 self-center">
                                                            <div className="flex flex-wrap items-center gap-1.5">
                                                                <span className="font-semibold text-slate-900">{org.name}</span>
                                                                {qrLive && (
                                                                    <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-800">
                                                                        Aktif
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {desc && (
                                                                <p className="mt-0.5 line-clamp-1 text-xs text-slate-600">{desc}</p>
                                                            )}
                                                        </div>
                                                        <span className="self-center text-slate-400" aria-hidden="true">
                                                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                            </svg>
                                                        </span>
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                                {query.trim().length > 0 && query.trim().length < 2 && (
                                    <p className="mt-1.5 text-xs text-slate-500">En az 2 karakter girin.</p>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                            <button
                                type="button"
                                onClick={onRetry}
                                className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 text-sm font-semibold text-white shadow-md shadow-violet-500/25 transition hover:brightness-105 sm:flex-none"
                            >
                                Tekrar dene
                            </button>
                            <Link
                                to="/"
                                className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800 transition hover:bg-slate-50 sm:flex-none"
                            >
                                Ana sayfaya dön
                            </Link>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
